import { LANDING_CODE_QUERY_KEY, LANDING_PERSIST_QUERY_KEYS } from '@/constants/landing-dye';
import { resolvePromotionCodeForDye } from '@/utils/landing/dye-query';

/** 可读取 query 的最小接口（URLSearchParams / ReadonlyURLSearchParams / 自建 map） */
export type TDyeQuerySource = Pick<URLSearchParams, 'get'>;

function getPersistQueryValue(search: TDyeQuerySource, key: string): string | null {
    if (key === LANDING_CODE_QUERY_KEY) {
        return resolvePromotionCodeForDye(search);
    }
    const value = search.get(key);
    return value == null || value === '' ? null : value;
}

/**
 * 将落地相关透传参合并进目标 href（landing_code + 三参 + landing_timestamp，不含 promotion_code）。
 */
export function withDyeQuery(href: string, source?: TDyeQuerySource | null): string {
    const search: TDyeQuerySource | null =
        source ?? (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null);

    if (!search) {
        return href;
    }

    let url: URL;
    try {
        url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://local.invalid');
    } catch {
        return href;
    }

    url.searchParams.delete('promotion_code');

    LANDING_PERSIST_QUERY_KEYS.forEach(key => {
        if (url.searchParams.has(key)) {
            return;
        }
        const value = getPersistQueryValue(search, key);
        if (!value) {
            return;
        }
        url.searchParams.set(key, value);
    });

    const next = `${url.pathname}${url.search}${url.hash}`;
    return next === href ? href : next;
}

/** 从当前（或给定）search 提取透传快照；code 写入 landing_code，不带 promotion_code */
export function pickLandingPersistParams(source?: TDyeQuerySource | null): Record<string, string> {
    const search: TDyeQuerySource | null =
        source ?? (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null);
    if (!search) {
        return {};
    }

    const result: Record<string, string> = {};
    LANDING_PERSIST_QUERY_KEYS.forEach(key => {
        const value = getPersistQueryValue(search, key);
        if (value) {
            result[key] = value;
        }
    });
    return result;
}

/** 用快照合并进 href；空对象不改 href */
export function withDyeQueryFromRecord(href: string, params?: Record<string, string> | null): string {
    if (!params || Object.keys(params).length === 0) {
        return href;
    }
    const normalized = { ...params };
    delete normalized.promotion_code;
    return withDyeQuery(href, new URLSearchParams(normalized));
}
